import { Link, useLocation } from 'react-router-dom';
import { usePortal } from '../lib/portal';
import { useListingPaths } from '../lib/listingPaths';
import { ExploreTabs } from '../components/ExploreTabs';
import { ExploreHero } from '../components/ExploreHero';
import { SectionIcon, type Section } from '../components/icons';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { Card } from '../components/ui';
import '../styles/public.css';

/**
 * "+ Post / Offer" — the unified posting entry point (specification §12).
 *
 * One question, four answers, so nobody has to know in advance which of four
 * posting systems they need. §12's words: "This should replace users having to
 * understand multiple separate posting systems."
 *
 * They are not equivalent underneath, and the page says so rather than hiding
 * it. A service or an item is free and takes a minute; renting out a unit needs
 * a NyumbaSuite SUBSCRIPTION, because a rental listing belongs to a real unit
 * inside a landlord's account; and selling a property needs neither — any
 * account may sell — but is PAID, once the listing has been approved.
 * Discovering any of that after filling in a form would be worse than reading
 * it before starting.
 *
 * These notes said "No account needed" for two days after signing up became
 * required. Copy that describes a rule is part of the rule: when the rule
 * changed and the sentence did not, the page was lying to every visitor who
 * read it.
 */

interface Option {
  to: string;
  title: string;
  blurb: string;
  note: string;
  section: Section;
}

export function PostSomething() {
  const { pathname } = useLocation();
  const { isAuthenticated } = usePortal();
  // A public account posts property from its own area; a landlord from Vacancies (156).
  const publicPoster = useListingPaths().publicPoster;

  const options: Option[] = [
    {
      to: !isAuthenticated ? '/services/signup?for=property'
        : publicPoster ? '/my-listings/homes/new' : '/vacancies',
      title: 'Post a home to let',
      blurb: 'A home to rent, or a short stay. Owners, agents, caretakers and individuals.',
      note: !isAuthenticated
        ? 'Create a free account first. You pay to advertise, and we approve it before it goes live.'
        : publicPoster
          ? 'You pay to advertise, and we approve it before it goes live.'
          : 'Opens your vacancies. An advert for a unit you manage goes live when you publish it.',
      section: 'homes',
    },
    {
      // Any account may sell a property — a seller is not a landlord, and
      // registerProviderAccount deliberately gives them none of a landlord's
      // furniture. So this door asks only for a sign-in.
      to: '/sell',
      title: 'Sell a property',
      blurb: 'A house, an apartment or a plot, advertised to buyers.',
      note: isAuthenticated
        ? 'Free to prepare. You pay to publish, after it is approved.'
        : 'Sign in first — it takes a minute. Free to prepare; you pay to publish.',
      section: 'buy',
    },
    {
      to: '/services/register',
      title: 'Offer a service',
      blurb: 'Plumbing, electrical, cleaning, moving and other trades.',
      note: isAuthenticated
        ? 'Free. We review it before it appears.'
        : 'Free. Sign in first — it takes a minute — then we review it before it appears.',
      section: 'services',
    },
    {
      to: '/marketplace/post',
      title: 'Sell something',
      blurb: 'Furniture, appliances and household things.',
      note: isAuthenticated
        ? 'Free. Listings run for 60 days.'
        : 'Free. Sign in first — it takes a minute. Listings run for 60 days.',
      section: 'market',
    },
  ];

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        {/* This sub-line describes a rule, so it is part of the rule — it read
            "two of the three are free" for two days after that stopped being
            true, and every visitor believed it. Four doors now, and two of them
            are free: a service and an item. */}
        <ExploreHero
          section="post"
          title="What do you want to offer?"
          sub="Pick one. Listing a service or an item is free and takes a minute."
        />

        <div className="post-choices">
          {options.map(o => (
            <Link key={o.to} to={o.to} className="post-choice">
              <SectionIcon section={o.section} size={22} />
              <span className="post-choice-body">
                <span className="post-choice-title">{o.title}</span>
                <span className="post-choice-blurb">{o.blurb}</span>
                <span className="post-choice-note">{o.note}</span>
              </span>
              <span className="post-choice-go" aria-hidden>›</span>
            </Link>
          ))}
        </div>

        <Card title="Why we review everything">
          <p style={{ margin: 0 }}>
            Every service and every item is read by somebody at NyumbaSuite before it goes
            live. It means your listing is not instant, and it means the directory is worth
            reading. We check a business is real; we do not inspect anybody's work.
          </p>
        </Card>
      </main>
      <SiteFooter />
    </>
  );
}
